import React, { createContext, useContext, useEffect, useState } from "react";

export const translations = {
  en: {
    nav: {
      problem: "Problem",
      solution: "Solution",
      design: "Design",
      howItWorks: "How it works",
      features: "Features",
      market: "Market",
      team: "Team",
      contact: "Contact",
      platform: "Management Platform",
    },
    hero: {
      overline: "Smart Parking. Re-engineered.",
      title1: "Reclaim every",
      titleHighlight: "parking space.",
      title2: "",
      subtitle:
        "LìBER is a connected parking solution that protects reserved spaces, eliminates conflict and turns any parking lot into a measurable, bookable, revenue-generating asset.",
      ctaPrimary: "Partner with us",
      ctaSecondary: "Access Management Platform",
      stat1: "Modular",
      stat1Label: "Platform|architecture",
      stat2: "Stylish",
      stat2Label: "Iconic urban|design",
      stat3: "Smart",
      stat3Label: "Connected &|autonomous",
    },
    problem: {
      overline: "The Problem",
      title: "Parking is broken — and it's costing everyone.",
      body: "Drivers waste millions of hours looking for a spot. Landlords lose revenue to unauthorized parking. Businesses can't guarantee spaces for clients, employees or EV chargers. Physical blockers are dumb, manual, and inconvenient.",
      stats: [
        { value: "17 min", label: "avg. time spent finding a parking spot in urban centers" },
        { value: "€1.3B", label: "lost annually to unauthorized parking in EU" },
        { value: "30%", label: "of urban traffic is just cars searching for parking" },
      ],
    },
    design: {
      overline: "Design Advantage",
      title: "A blocker you'd actually want on your street.",
      body: "Every other smart blocker on the market looks like a yellow industrial clamp. LìBER is an object you want to see in your plaza. Clean, flowing lines, a base made of concrete or concrete composite that matches the urban furniture around it, no visible fasteners — and custom finishes that sit as naturally on a modern campus as on a historical city square.",
      variants: [
        { k: "Urban / Modern", d: "The look of modern urban furniture. Fits offices, malls, new developments." },
        { k: "Historical / Heritage", d: "Same materials, local colors: a base matching the basalt paving, an arm in the tones of the surrounding buildings." },
        { k: "Custom OEM", d: "Private-label colorways and engraved logo for operators and municipalities." },
      ],
    },
    solution: {
      overline: "The Solution",
      title: "One modular device. One platform. Full control.",
      body: "LìBER is a ground-mounted smart blocker built as a modular platform — swap design, comms or power independently. Paired with our cloud, any space becomes addressable, bookable and measurable, from a single bay to a 500-bay enterprise lot.",
      specs: [
        { k: "Dimensions", v: "465 × 235 × 65 mm" },
        { k: "Load rating", v: "Up to 500 kg wheel load" },
        { k: "Connectivity", v: "LoRa · BLE · LTE · Wi-Fi · ETH" },
        { k: "Power", v: "Swappable battery (1 yr+) or PoE" },
        { k: "Ingress", v: "IP67 sealed" },
        { k: "Modularity", v: "Field-swappable modules" },
      ],
    },
    how: {
      overline: "How it works",
      title: "From reservation to release in four steps.",
      steps: [
        { n: "01", t: "Reserve", d: "Driver books a LìBER-protected space through the app, web or partner API." },
        { n: "02", t: "Arrive", d: "As the driver approaches, the blocker lowers automatically via geofence or one-tap." },
        { n: "03", t: "Park", d: "The space is claimed. Unauthorized vehicles are physically prevented from taking it." },
        { n: "04", t: "Leave", d: "On exit, the blocker rises. Billing, analytics and turnover are logged in real time." },
      ],
    },
    features: {
      overline: "Features",
      title: "Engineered for real-world parking lots.",
      items: [
        { t: "Modular platform", d: "Design, comms and power are independent, field-swappable modules. Upgrade without digging up the street." },
        { t: "Any connectivity", d: "LoRa, BLE, LTE, Wi-Fi or Ethernet — the same chassis, the right radio per site." },
        { t: "Flexible power", d: "A swappable battery sized for your maintenance cycle — 1 year and beyond — or Power over Ethernet." },
        { t: "Anti-theft & vandal-proof", d: "Flexible, not rigid: the arm gives way under force instead of breaking, so it cannot be destroyed. Anchored mounting, tamper sensors with instant alerts." },
        { t: "Real-time telemetry", d: "Every raise, lower, occupancy and battery level streamed to your dashboard." },
        { t: "Open integrations", d: "Native in EV-charging apps, municipal parking, private operators — or our own LìBER app." },
      ],
    },
    market: {
      overline: "Market & Business model",
      title: "A €12B EU market going digital.",
      stats: [
        {
          k: "Total Addressable Market (TAM)",
          v: "€9–12B",
          sub: "EU parking control & monetization",
          d: "All EU parking spaces that can be digitally controlled and monetized.",
        },
        {
          k: "Serviceable Addressable Market (SAM)",
          v: "€2.8–5.6B",
          sub: "Open & uncontrolled spaces",
          d: "Spaces where barriers or cameras are not viable and control is needed.",
        },
        {
          k: "Serviceable Obtainable Market (SOM)",
          v: "€12–23M",
          sub: "Romania + Germany · 5 years",
          d: "Realistic revenue based on targeted adoption of subscription-based parking control.",
        },
      ],
      streamsTitle: "Revenue streams",
      streams: [
        "HaaS / Platform-as-a-Service — bundled device + platform, monthly",
        "Hardware sales — one-time per blocker",
        "SaaS subscription — platform only, per device / month",
        "Revenue share on bookings & EV charging — via partner apps or our own LìBER app (payments included)",
        "Enterprise & municipality licences",
      ],
      pricingTitle: "Starter pricing",
      pricing: [
        { k: "Blocker (HW)", v: "€700" },
        { k: "Platform (SaaS)", v: "€9 / dev / mo" },
        { k: "HaaS (all-in)", v: "€19 / dev / mo" },
        { k: "Enterprise", v: "Custom" },
      ],
    },
    team: {
      overline: "Team",
      title: "Business strategy, operations, innovation and product development professionals.",
      body: "A team combining embedded engineering, cloud platforms, business and product management.",
      members: [
        { n: "Silvana Speianu", r: "CEO & Co-Founder", b: "Ex-management consultant and entrepreneur. Co-Founder of a wood products manufacturing company." },
        { n: "Tom Rudolph", r: "CTO & Co-Founder", b: "Embedded + IoT. Led multiple development teams in large consumer goods companies; co-founder and investor in multiple tech start-ups." },
        { n: "Paul Druta", r: "Product Development Partner", b: "Co-Founder of Lineo Engineering: a product development company." },
      ],
    },
    contact: {
      overline: "Let's talk",
      title: "Build with us.",
      body: "Investor, integrator, landlord or city — drop a note and we'll reply within 48 hours.",
      name: "Full name",
      email: "Email",
      role: "I am a…",
      company: "Company (optional)",
      message: "Tell us what you have in mind",
      submit: "Send message",
      submitting: "Sending…",
      success: "Thanks — we'll be in touch shortly.",
      error: "Something went wrong. Please try again.",
      roles: ["Investor", "Collaborator / Partner", "Landlord / Operator", "City / Public sector", "Driver / End user", "Other"],
    },
    footer: {
      rights: "All rights reserved.",
      imprint: "Imprint",
      privacy: "Privacy Policy",
      tagline: "Smart parking, quietly powerful.",
    },
    legal: {
      back: "Back to site",
      updated: "Last updated",
      englishOnly: "",
    },
    platform: {
      title: "Management Platform",
      subtitle: "Login — coming soon",
      body: "The LìBER operator platform is in private beta. Request access and we'll onboard you personally.",
      cta: "Request access",
      back: "Back to site",
    },
  },
  de: {
    nav: {
      problem: "Problem",
      solution: "Lösung",
      design: "Design",
      howItWorks: "Funktionsweise",
      features: "Funktionen",
      market: "Markt",
      team: "Team",
      contact: "Kontakt",
      platform: "Management-Plattform",
    },
    hero: {
      overline: "Smart Parking. Neu gedacht.",
      title1: "Sichern Sie jeden",
      titleHighlight: "Parkplatz.",
      title2: "",
      subtitle:
        "LìBER ist eine vernetzte Parklösung, die reservierte Plätze schützt, Konflikte vermeidet und jeden Parkplatz in ein messbares, buchbares, umsatzgenerierendes Asset verwandelt.",
      ctaPrimary: "Partner werden",
      ctaSecondary: "Zur Management-Plattform",
      stat1: "Modular",
      stat1Label: "Plattform-|Architektur",
      stat2: "Stilvoll",
      stat2Label: "Ikonisches|Stadtdesign",
      stat3: "Smart",
      stat3Label: "Voll|autonom",
    },
    problem: {
      overline: "Das Problem",
      title: "Parken ist kaputt — und teuer für alle.",
      body: "Fahrer verlieren Stunden bei der Platzsuche. Vermieter verlieren Umsatz durch Falschparker. Unternehmen können Plätze für Kunden, Mitarbeiter oder E-Ladesäulen nicht garantieren. Klassische Sperren sind dumm, manuell und unpraktisch.",
      stats: [
        { value: "17 Min.", label: "durchschnittliche Parkplatzsuche in Städten" },
        { value: "1,3 Mrd. €", label: "jährlicher Verlust durch Falschparken in der EU" },
        { value: "30%", label: "des Stadtverkehrs ist reine Parkplatzsuche" },
      ],
    },
    design: {
      overline: "Design-Vorsprung",
      title: "Eine Parksperre, die Sie tatsächlich in Ihrer Straße sehen wollen.",
      body: "Alle anderen smarten Sperren auf dem Markt sehen aus wie eine gelbe Industrieklemme. LìBER ist ein Objekt, das Sie in Ihrer Plaza sehen wollen. Klare, fließende Linien, ein Sockel aus Beton oder Betonverbundwerkstoff, der zum umgebenden Stadtmobiliar passt, keine sichtbaren Schrauben — und individuelle Oberflächen, die auf einem modernen Campus genauso natürlich wirken wie auf einem historischen Stadtplatz.",
      variants: [
        { k: "Urban / Modern", d: "Im Stil modernen Stadtmobiliars. Passt zu Büros, Malls, Neubauten." },
        { k: "Historisch / Heritage", d: "Gleiche Materialien, lokale Farben: Sockel passend zum Basaltpflaster, Bügel im Ton der umliegenden Gebäude." },
        { k: "Custom OEM", d: "Private-Label-Farben und gravierte Logos für Betreiber und Kommunen." },
      ],
    },
    solution: {
      overline: "Die Lösung",
      title: "Ein modulares Gerät. Eine Plattform. Volle Kontrolle.",
      body: "LìBER ist eine bodenmontierte Sperre — als modulare Plattform konstruiert, bei der Design, Kommunikation und Stromversorgung unabhängig austauschbar sind. In Kombination mit unserer Cloud wird jeder Stellplatz adressierbar, buchbar und messbar — vom einzelnen Platz bis zum 500-Platz-Unternehmensparkplatz.",
      specs: [
        { k: "Abmessungen", v: "465 × 235 × 65 mm" },
        { k: "Belastbarkeit", v: "Bis 500 kg Radlast" },
        { k: "Konnektivität", v: "LoRa · BLE · LTE · Wi-Fi · ETH" },
        { k: "Stromversorgung", v: "Wechselakku (1 J+) oder PoE" },
        { k: "Schutzart", v: "IP67" },
        { k: "Modularität", v: "Feldtausch-Module" },
      ],
    },
    how: {
      overline: "Funktionsweise",
      title: "Von der Reservierung zum Verlassen in vier Schritten.",
      steps: [
        { n: "01", t: "Reservieren", d: "Der Fahrer bucht einen LìBER-geschützten Platz über App, Web oder Partner-API." },
        { n: "02", t: "Ankommen", d: "Bei Annäherung senkt sich die Sperre automatisch per Geofence oder ein Tipp." },
        { n: "03", t: "Parken", d: "Der Platz ist belegt. Unbefugte Fahrzeuge werden physisch abgewehrt." },
        { n: "04", t: "Verlassen", d: "Beim Verlassen fährt die Sperre hoch. Abrechnung und Analytik in Echtzeit." },
      ],
    },
    features: {
      overline: "Funktionen",
      title: "Entwickelt für reale Parkplätze.",
      items: [
        { t: "Modulare Plattform", d: "Design, Kommunikation und Stromversorgung sind unabhängige, feldtauschbare Module. Aufrüsten ohne die Straße aufzureißen." },
        { t: "Beliebige Konnektivität", d: "LoRa, BLE, LTE, Wi-Fi oder Ethernet — dasselbe Chassis, der richtige Funk pro Standort." },
        { t: "Flexible Stromversorgung", d: "Wechselakku, dimensioniert auf Ihren Wartungszyklus — 1 Jahr und mehr — oder Power over Ethernet." },
        { t: "Diebstahl- & vandalismussicher", d: "Flexibel statt starr: Der Bügel gibt bei Gewalt nach, statt zu brechen, und lässt sich so nicht zerstören. Verankerte Montage, Sabotagesensoren mit Sofort-Alarm." },
        { t: "Echtzeit-Telemetrie", d: "Jede Bewegung, Belegung und Akkuladung wird live ins Dashboard gestreamt." },
        { t: "Offene Integrationen", d: "Native Einbindung in E-Lade-Apps, kommunale Parkbetreiber, private Betreiber — oder unsere eigene LìBER-App." },
      ],
    },
    market: {
      overline: "Markt & Geschäftsmodell",
      title: "Ein 12 Mrd. € EU-Markt wird digital.",
      stats: [
        {
          k: "Gesamter adressierbarer Markt (TAM)",
          v: "€9–12 Mrd.",
          sub: "EU Parkkontrolle & Monetarisierung",
          d: "Alle EU-Parkplätze, die digital kontrolliert und monetarisiert werden können.",
        },
        {
          k: "Bedienbarer adressierbarer Markt (SAM)",
          v: "€2,8–5,6 Mrd.",
          sub: "Offene & unkontrollierte Plätze",
          d: "Plätze, an denen Schranken oder Kameras nicht praktikabel sind und Kontrolle benötigt wird.",
        },
        {
          k: "Bedienbarer erreichbarer Markt (SOM)",
          v: "€12–23 Mio.",
          sub: "Rumänien + Deutschland · 5 Jahre",
          d: "Realistische Umsätze basierend auf gezielter Adoption von abonnementbasierter Parkkontrolle.",
        },
      ],
      streamsTitle: "Umsatzquellen",
      streams: [
        "HaaS / Platform-as-a-Service — Gerät + Plattform im Paket, monatlich",
        "Hardware-Verkauf — einmalig pro Sperre",
        "SaaS-Abo — nur Plattform, pro Gerät / Monat",
        "Revenue Share bei Buchungen & E-Laden — über Partner-Apps oder unsere eigene LìBER-App (inkl. Zahlungsabwicklung)",
        "Enterprise- & Kommunal-Lizenzen",
      ],
      pricingTitle: "Einstiegspreise",
      pricing: [
        { k: "Sperre (HW)", v: "700 €" },
        { k: "Plattform (SaaS)", v: "9 € / Gerät / Mon." },
        { k: "HaaS (all-in)", v: "19 € / Gerät / Mon." },
        { k: "Enterprise", v: "Individuell" },
      ],
    },
    team: {
      overline: "Team",
      title: "Business-Strategie, Operations und Produktentwicklungs-Profis.",
      body: "Ein Team aus Embedded-Engineering, Cloud-Plattformen, Business- und Produktmanagement.",
      members: [
        { n: "Silvana Speianu", r: "CEO & Mitgründerin", b: "Ex-Unternehmensberaterin und Unternehmerin. Mitgründerin eines Holzprodukte-Herstellers." },
        { n: "Tom Rudolph", r: "CTO & Mitgründer", b: "Embedded + IoT. Leitete mehrere Entwicklungsteams in großen Konsumgüterunternehmen; Mitgründer und Investor in mehreren Tech-Startups." },
        { n: "Paul Druta", r: "Product Development Partner", b: "Mitgründer von Lineo Engineering: einem Produktentwicklungsunternehmen." },
      ],
    },
    contact: {
      overline: "Sprechen wir",
      title: "Bauen Sie mit uns.",
      body: "Investor, Integrator, Vermieter oder Stadt — hinterlassen Sie eine Nachricht, wir antworten binnen 48 Stunden.",
      name: "Vollständiger Name",
      email: "E-Mail",
      role: "Ich bin ein…",
      company: "Unternehmen (optional)",
      message: "Erzählen Sie uns von Ihrem Vorhaben",
      submit: "Nachricht senden",
      submitting: "Senden…",
      success: "Danke — wir melden uns in Kürze.",
      error: "Etwas ist schiefgelaufen. Bitte erneut versuchen.",
      roles: ["Investor", "Partner / Kollaborateur", "Vermieter / Betreiber", "Stadt / Öffentlicher Sektor", "Fahrer / Endnutzer", "Andere"],
    },
    footer: {
      rights: "Alle Rechte vorbehalten.",
      imprint: "Impressum",
      privacy: "Datenschutz",
      tagline: "Smart Parking, leise stark.",
    },
    legal: {
      back: "Zur Startseite",
      updated: "Stand",
      englishOnly: "Dieser Text ist nur auf Englisch verfügbar.",
    },
    platform: {
      title: "Management-Plattform",
      subtitle: "Login — in Kürze",
      body: "Die LìBER-Betreiber-Plattform ist in geschlossener Beta. Fordern Sie Zugang an, wir begleiten Sie persönlich.",
      cta: "Zugang anfragen",
      back: "Zur Startseite",
    },
  },
  ro: {
    nav: {
      problem: "Problema",
      solution: "Soluția",
      design: "Design",
      howItWorks: "Cum funcționează",
      features: "Funcții",
      market: "Piață",
      team: "Echipă",
      contact: "Contact",
      platform: "Platformă management",
    },
    hero: {
      overline: "Parcare inteligentă. Reinventată.",
      title1: "Recuperează orice",
      titleHighlight: "loc de parcare.",
      title2: "",
      subtitle:
        "LìBER este o soluție de parcare conectată care protejează locurile rezervate, elimină conflictele și transformă orice parcare într-un activ măsurabil, rezervabil și generator de venituri.",
      ctaPrimary: "Devino partener",
      ctaSecondary: "Accesează platforma",
      stat1: "Modular",
      stat1Label: "Arhitectură|platformă",
      stat2: "Stilat",
      stat2Label: "Design urban|iconic",
      stat3: "Smart",
      stat3Label: "Complet|autonom",
    },
    problem: {
      overline: "Problema",
      title: "Parcarea a devenit ineficientă — și costă pe toată lumea.",
      body: "Șoferii pierd ore întregi căutând loc. Proprietarii pierd venituri din cauza parcării neautorizate. Firmele nu pot garanta locuri pentru clienți, angajați sau încărcare EV. Blocatoarele clasice sunt proaste, manuale și incomode.",
      stats: [
        { value: "17 min", label: "timp mediu de căutare a locului în orașe" },
        { value: "1,3 mld. €", label: "pierduți anual din parcare neautorizată în UE" },
        { value: "30%", label: "din traficul urban e căutare de loc de parcare" },
      ],
    },
    design: {
      overline: "Avantaj de design",
      title: "Un blocator pe care chiar l-ai vrea pe stradă.",
      body: "Toate celelalte blocatoare inteligente de pe piață arată ca o clemă industrială galbenă. LìBER e un obiect pe care vrei să-l vezi în piața ta. Linii curate, fluide, o bază din beton sau compozit de beton, asortată cu mobilierul urban din jur, fără șuruburi vizibile — și finisaje personalizate care se integrează la fel de natural într-un campus modern ca și într-o piață istorică.",
      variants: [
        { k: "Urban / Modern", d: "Aspectul mobilierului urban modern. Se potrivește birourilor, mall-urilor, dezvoltărilor noi." },
        { k: "Istoric / Patrimoniu", d: "Aceleași materiale, culori locale: baza asortată cu pavajul din bazalt, brațul în tonurile clădirilor din jur." },
        { k: "OEM Custom", d: "Coloristică private-label și logo gravat pentru operatori și municipalități." },
      ],
    },
    solution: {
      overline: "Soluția",
      title: "Un dispozitiv modular. O platformă. Control total.",
      body: "LìBER este un blocator montat în asfalt, construit ca platformă modulară — design, comunicații și alimentare se schimbă independent. Împreună cu platforma noastră cloud, orice loc devine adresabil, rezervabil și măsurabil — de la un singur loc până la parcări corporate de 500 de locuri.",
      specs: [
        { k: "Dimensiuni", v: "465 × 235 × 65 mm" },
        { k: "Sarcină", v: "Până la 500 kg pe roată" },
        { k: "Conectivitate", v: "LoRa · BLE · LTE · Wi-Fi · ETH" },
        { k: "Alimentare", v: "Baterie schimbabilă (1 an+) sau PoE" },
        { k: "Protecție", v: "IP67" },
        { k: "Modularitate", v: "Module schimbabile în teren" },
      ],
    },
    how: {
      overline: "Cum funcționează",
      title: "De la rezervare la eliberare în patru pași.",
      steps: [
        { n: "01", t: "Rezervă", d: "Șoferul rezervă un loc protejat LìBER prin aplicație, web sau API partener." },
        { n: "02", t: "Sosește", d: "La apropiere, blocatorul coboară automat prin geofence sau un singur tap." },
        { n: "03", t: "Parchează", d: "Locul e ocupat. Vehiculele neautorizate sunt oprite fizic." },
        { n: "04", t: "Pleacă", d: "La ieșire, blocatorul se ridică. Facturare și analiză în timp real." },
      ],
    },
    features: {
      overline: "Funcții",
      title: "Gândit pentru parcări reale.",
      items: [
        { t: "Platformă modulară", d: "Designul, comunicațiile și alimentarea sunt module independente, schimbabile în teren. Actualizezi fără să scoți asfaltul." },
        { t: "Orice conectivitate", d: "LoRa, BLE, LTE, Wi-Fi sau Ethernet — același chassis, radioul potrivit pentru fiecare locație." },
        { t: "Alimentare flexibilă", d: "Baterie schimbabilă, dimensionată pe ciclul tău de mentenanță — 1 an și peste — sau Power over Ethernet." },
        { t: "Anti-furt & anti-vandal", d: "Flexibil, nu rigid: brațul cedează la forță în loc să se rupă, așa că nu poate fi distrus. Montare ancorată, senzori anti-manipulare cu alertă instant." },
        { t: "Telemetrie în timp real", d: "Fiecare mișcare, ocupare și nivel baterie transmise live în dashboard." },
        { t: "Integrări deschise", d: "Nativ în aplicații de încărcare EV, parcări municipale, operatori privați — sau aplicația noastră LìBER." },
      ],
    },
    market: {
      overline: "Piață & model de business",
      title: "O piață UE de 12 mld. € care se digitalizează.",
      stats: [
        {
          k: "Piață totală adresabilă (TAM)",
          v: "€9–12 mld.",
          sub: "Control & monetizare parcare UE",
          d: "Toate locurile de parcare din UE care pot fi controlate digital și monetizate.",
        },
        {
          k: "Piață adresabilă servabilă (SAM)",
          v: "€2,8–5,6 mld.",
          sub: "Locuri deschise & necontrolate",
          d: "Locuri unde barierele sau camerele nu sunt viabile și e nevoie de control.",
        },
        {
          k: "Piață obtenabilă servabilă (SOM)",
          v: "€12–23 mil.",
          sub: "România + Germania · 5 ani",
          d: "Venituri realiste bazate pe adoptarea țintită a controlului parcării prin abonament.",
        },
      ],
      streamsTitle: "Surse de venit",
      streams: [
        "HaaS / Platform-as-a-Service — dispozitiv + platformă la pachet, lunar",
        "Vânzare hardware — unică per blocator",
        "Abonament SaaS — doar platforma, per dispozitiv / lună",
        "Revenue share pe rezervări & încărcări EV — prin aplicații partenere sau aplicația noastră LìBER (inclusiv procesarea plăților)",
        "Licențe enterprise & municipalități",
      ],
      pricingTitle: "Prețuri de pornire",
      pricing: [
        { k: "Blocator (HW)", v: "700 €" },
        { k: "Platformă (SaaS)", v: "9 € / disp. / lună" },
        { k: "HaaS (all-in)", v: "19 € / disp. / lună" },
        { k: "Enterprise", v: "La cerere" },
      ],
    },
    team: {
      overline: "Echipă",
      title: "Profesioniști în strategie de business, operațiuni, inovație și dezvoltare de produs.",
      body: "O echipă reunind inginerie embedded, platforme cloud, business și product management.",
      members: [
        { n: "Silvana Speianu", r: "CEO & Co-fondatoare", b: "Fostă consultantă de management și antreprenoare. Co-fondatoare a unei companii de producție de produse din lemn." },
        { n: "Tom Rudolph", r: "CTO & Co-fondator", b: "Embedded + IoT. A condus multiple echipe de dezvoltare în companii mari de bunuri de consum; co-fondator și investitor în multiple startup-uri tech." },
        { n: "Paul Druta", r: "Product Development Partner", b: "Co-fondator al Lineo Engineering: o companie de dezvoltare de produs." },
      ],
    },
    contact: {
      overline: "Hai să vorbim",
      title: "Construiește alături de noi.",
      body: "Investitor, integrator, proprietar sau autoritate locală — lasă-ne un mesaj și revenim în 48 de ore.",
      name: "Nume complet",
      email: "Email",
      role: "Sunt…",
      company: "Companie (opțional)",
      message: "Spune-ne ce ai în minte",
      submit: "Trimite mesaj",
      submitting: "Se trimite…",
      success: "Mulțumim — revenim în curând.",
      error: "Ceva nu a mers. Încearcă din nou.",
      roles: ["Investitor", "Colaborator / Partener", "Proprietar / Operator", "Autoritate / Sector public", "Șofer / Utilizator final", "Altceva"],
    },
    footer: {
      rights: "Toate drepturile rezervate.",
      imprint: "Informații legale",
      privacy: "Politica de confidențialitate",
      tagline: "Parcare inteligentă, discret puternică.",
    },
    legal: {
      back: "Înapoi la site",
      updated: "Ultima actualizare",
      englishOnly: "Acest text este disponibil doar în limba engleză.",
    },
    platform: {
      title: "Platformă de management",
      subtitle: "Autentificare — în curând",
      body: "Platforma LìBER pentru operatori e în beta privată. Solicită acces și te integrăm personal.",
      cta: "Cere acces",
      back: "Înapoi la site",
    },
  },
};

const I18nContext = createContext({ lang: "en", setLang: () => {}, t: translations.en });

export const I18nProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return "en";
    return localStorage.getItem("liber_lang") || "en";
  });

  useEffect(() => {
    if (typeof window !== "undefined") localStorage.setItem("liber_lang", lang);
    if (typeof document !== "undefined") document.documentElement.lang = lang;
  }, [lang]);

  const value = { lang, setLang, t: translations[lang] || translations.en };
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);
